import { ReactNode } from 'react';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { PreApprovalStore } from '@servicetitan/financing-plans-shared';
import { isGreenSkyProvider } from '@servicetitan/financing-utils-shared';
import { useLoadPreApprovals } from '../../hooks/useLoadPreApprovals';

/**
 * Populates the pre-approval store for the application MFEs.
 *
 * Reads the setup data, loads the customer's latest pre-approval (skipping prequal/loan launch flows),
 * and provides it to `PreApprovalStore` so `usePreApprovalDisplay` (in plans-shared) can read from a
 * single source of truth. Other MFEs that reuse the plans components simply don't mount this loader,
 * so the store defaults to "no pre-approval" and no pre-approval components are displayed.
 */
export const PreApprovalLoader = ({ children }: { children: ReactNode }) => {
    const { setupData } = SetupDataStore.useStore();

    // Pre-approvals are GreenSky-only and consumed only on the GreenSky plans page (usePreApprovalDisplay
    // gate 3 uses the same provider check), so gate the fetch on GreenSky to avoid a wasted
    // /v1/preapprovals call on every non-GreenSky application launch. Reactive: switching to GreenSky
    // re-enables it, and the per-customer query key is unchanged so no duplicate cache entry is created.
    // Also skip prequal/loan launch flows; those are special cases that never show pre-approvals.
    const enabled =
        isGreenSkyProvider(setupData?.provider) &&
        !setupData?.prequalificationId &&
        !setupData?.loanId;

    const { data } = useLoadPreApprovals(
        setupData?.tenantId ?? '',
        setupData?.customerId ?? '',
        setupData?.buId ?? '',
        { enabled },
    );

    return (
        <PreApprovalStore.Provider preApproval={data ?? null}>{children}</PreApprovalStore.Provider>
    );
};
